/**
 * 공지사항 첨부 — 상세 다이얼로그 안에서 파일을 올리고 받는다.
 *
 * 저장소는 현장 파일 보관함과 같다(site_documents). 다른 것은 매다는 대상뿐으로,
 * schedule_id 가 붙으면 이 공지의 첨부가 되고 보관함 목록에서는 빠진다. 그래서
 * 업로드·다운로드·삭제 API 를 새로 만들지 않고 그대로 쓴다.
 *
 * 다운로드는 서비스가 인증 헤더로 blob 을 받아 원본 파일명으로 저장한다 —
 * <a download> 는 cross-origin 에서 파일명을 무시한다.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  UploadFile as UploadIcon,
} from '@mui/icons-material';

import { SiteDocumentService } from '../../services/SiteDocumentService';
import { SiteDocument } from '../../models/SiteDocument';

export interface ScheduleAttachmentsProps {
  scheduleId: number;
  /** 업로드가 어느 현장 보관함으로 갈지 — 공지가 속한 현장. */
  projectId: number;
  /** admin/editor 만 올리고 지운다 — 백엔드도 같은 역할로 막는다. */
  writable?: boolean;
}

const isImage = (d: SiteDocument) => (d.mime_type || '').startsWith('image/');

function humanSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  const u = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v < 10 && i > 0 ? v.toFixed(1) : Math.round(v)}${u[i]}`;
}

const ScheduleAttachments: React.FC<ScheduleAttachmentsProps> = ({
  scheduleId,
  projectId,
  writable = false,
}) => {
  const [docs, setDocs] = useState<SiteDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // 여기서 setError(null) 을 하지 않는다. 업로드가 "일부 실패" 를 알린 직후
  // 목록을 다시 읽는데, 그때 오류를 지우면 사람이 실패 사실을 못 본다.
  // 오류는 각 동작 시작 시점에서만 지운다.
  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    SiteDocumentService.listBySchedule(scheduleId)
      .then((list) => {
        if (!cancelled) setDocs(list);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '첨부 조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scheduleId]);

  useEffect(() => load(), [load]);

  // 여러 개를 한 번에 고르면 하나씩 올린다. 하나가 실패해도 나머지는 올라가야
  // 하므로 통째로 중단하지 않고, 실패한 파일 이름만 모아 보여 준다.
  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    const failed: string[] = [];
    for (const f of Array.from(files)) {
      try {
        await SiteDocumentService.upload(projectId, f, { scheduleId });
      } catch {
        failed.push(f.name);
      }
    }
    setBusy(false);
    if (failed.length) setError(`올리지 못한 파일: ${failed.join(', ')}`);
    load();
  };

  const remove = async (d: SiteDocument) => {
    if (!window.confirm(`"${d.original_name}" 을(를) 삭제할까요?`)) return;
    setBusy(true);
    setError(null);
    try {
      await SiteDocumentService.delete(d.id);
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '삭제 실패');
    } finally {
      setBusy(false);
    }
  };

  const download = async (d: SiteDocument) => {
    setError(null);
    try {
      await SiteDocumentService.download(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : '다운로드 실패');
    }
  };

  return (
    <Box data-testid="schedule-attachments">
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <AttachIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" fontWeight={700}>
          첨부 {docs.length > 0 && `(${docs.length})`}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {writable && (
          <>
            <input
              ref={fileRef}
              type="file"
              multiple
              hidden
              onChange={(e) => {
                upload(e.target.files);
                e.target.value = ''; // 같은 파일을 다시 골라도 change 가 뜨게
              }}
            />
            <Button
              size="small"
              startIcon={busy ? <CircularProgress size={14} /> : <UploadIcon fontSize="small" />}
              onClick={() => fileRef.current?.click()}
              disabled={busy}
            >
              {busy ? '올리는 중…' : '파일 추가'}
            </Button>
          </>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
          <CircularProgress size={18} />
        </Box>
      ) : docs.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          첨부된 파일이 없습니다.
          {writable && ' 이미지·문서 모두 올릴 수 있습니다.'}
        </Typography>
      ) : (
        <Stack spacing={0.75}>
          {docs.map((d) => (
            <Stack
              key={d.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{
                px: 1,
                py: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                minWidth: 0,
              }}
            >
              {isImage(d) && <Chip label="이미지" size="small" variant="outlined" sx={{ height: 20 }} />}
              <Tooltip title={d.original_name}>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  underline="hover"
                  onClick={() => download(d)}
                  sx={{
                    textAlign: 'left',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {d.original_name}
                </Link>
              </Tooltip>
              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                {humanSize(d.size_bytes)}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <IconButton size="small" onClick={() => download(d)} aria-label={`${d.original_name} 다운로드`}>
                <DownloadIcon fontSize="small" />
              </IconButton>
              {writable && (
                <IconButton
                  size="small"
                  onClick={() => remove(d)}
                  disabled={busy}
                  aria-label={`${d.original_name} 삭제`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ScheduleAttachments;
